import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Monitor, Shield, BarChart3, Clock, AlertTriangle, CheckCircle, ArrowRight, FileText } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

export default function TestEnginePage() {
    const navigate = useNavigate()

    const testSeries = [
        {
            id: 'neet-2025-full',
            title: 'NEET 2025 Full Length Mock Tests',
            description: 'Complete mock tests following NTA pattern with 200 questions',
            tests: 50,
            duration: '3 hours 20 minutes',
            pattern: 'NTA Official Pattern',
            subjects: ['Physics', 'Chemistry', 'Biology'],
            badge: 'Most Popular',
        },
        {
            id: 'neet-chapter-wise',
            title: 'Chapter-wise Practice Tests',
            description: 'Subject and chapter-specific tests for targeted practice',
            tests: 150,
            duration: '45-60 minutes',
            pattern: 'Chapter-wise',
            subjects: ['Physics', 'Chemistry', 'Biology'],
            badge: 'Recommended',
        },
        {
            id: 'neet-previous-year',
            title: 'Previous Year Papers (2015-2024)',
            description: 'Solve actual NEET papers from past 10 years',
            tests: 10,
            duration: '3 hours 20 minutes',
            pattern: 'Actual NEET Papers',
            subjects: ['Physics', 'Chemistry', 'Biology'],
            badge: 'Essential',
        },
        {
            id: 'neet-grand-test',
            title: 'Grand Test Series',
            description: 'High-difficulty tests for advanced preparation',
            tests: 25,
            duration: '3 hours 20 minutes',
            pattern: 'Advanced Level',
            subjects: ['Physics', 'Chemistry', 'Biology'],
            badge: 'Advanced',
        },
    ]

    const features = [
        {
            icon: Monitor,
            title: 'NTA Pattern Interface',
            description: 'Exact replica of official NEET exam interface',
        },
        {
            icon: Clock,
            title: 'Real-time Timer',
            description: 'Section-wise and overall timer with alerts',
        },
        {
            icon: Shield,
            title: 'Secure Testing',
            description: 'Anti-cheating measures and browser restrictions',
        },
        {
            icon: BarChart3,
            title: 'Detailed Analytics',
            description: 'Comprehensive performance analysis and insights',
        },
    ]

    const handleStartTest = (testId: string) => {
        // Navigate to test instructions page
        navigate(`/test/instructions/${testId}`)
    }

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-16 overflow-hidden">


                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Test Practice Centre
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">NEET Mock Test</span>
                            <span className="block mt-2 gradient-text">Practice Centre</span>
                        </h1>
                        <p className="text-xl text-theme-secondary mb-8">
                            Experience the actual NEET exam environment with our NTA-pattern test interface
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 text-center hover:scale-105 transition-all duration-300">
                                        <Icon className="h-8 w-8 text-primary-blue mx-auto mb-3" />
                                        <h3 className="text-lg font-semibold text-theme-primary mb-2">{feature.title}</h3>
                                        <p className="text-sm text-theme-muted">{feature.description}</p>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Test Series Selection */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Choose Your <span className="gradient-text">Test Series</span>
                        </h2>
                        <p className="text-lg text-theme-secondary">
                            Select from our comprehensive collection of NEET mock tests
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        {testSeries.map((test, index) => (
                            <motion.div
                                key={test.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="p-8 hover:scale-105 transition-all duration-300 h-full flex flex-col">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <h3 className="text-2xl font-display font-semibold text-theme-primary mb-2">
                                                {test.title}
                                            </h3>
                                            <p className="text-theme-secondary mb-4">{test.description}</p>
                                        </div>
                                        {test.badge && (
                                            <span className="px-3 py-1 bg-accent-gold text-slate-900 text-xs font-semibold rounded-full whitespace-nowrap ml-4">
                                                {test.badge}
                                            </span>
                                        )}
                                    </div>

                                    <div className="space-y-3 mb-6">
                                        <div className="flex items-center gap-2 text-theme-muted">
                                            <FileText className="h-4 w-4 text-primary-blue" />
                                            <span className="text-sm">{test.tests} Tests Available</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-theme-muted">
                                            <Clock className="h-4 w-4 text-primary-blue" />
                                            <span className="text-sm">Duration: {test.duration}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-theme-muted">
                                            <CheckCircle className="h-4 w-4 text-primary-blue" />
                                            <span className="text-sm">Pattern: {test.pattern}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 mb-6">
                                        {test.subjects.map((subject, idx) => (
                                            <span key={idx} className="px-3 py-1 bg-slate-100 dark:bg-slate-800/50 text-sm text-slate-600 dark:text-slate-300 rounded">
                                                {subject}
                                            </span>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className="mt-auto w-full px-6 py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                                    >
                                        Start Test
                                        <ArrowRight className="h-5 w-5" />
                                    </button>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            How It <span className="gradient-text">Works</span>
                        </h2>
                    </motion.div>

                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-6">
                            {[
                                {
                                    step: '1',
                                    title: 'Select Test Series',
                                    description: 'Choose from full-length mocks, chapter-wise tests, or previous year papers',
                                },
                                {
                                    step: '2',
                                    title: 'Read Instructions',
                                    description: 'Review exam pattern, marking scheme, and important guidelines',
                                },
                                {
                                    step: '3',
                                    title: 'Enter Credentials',
                                    description: 'Provide your name and roll number to start the test',
                                },
                                {
                                    step: '4',
                                    title: 'Take the Test',
                                    description: 'Answer questions in the NTA-pattern interface with real-time timer',
                                },
                                {
                                    step: '5',
                                    title: 'Submit & Review',
                                    description: 'Submit your test and get instant detailed performance analysis',
                                },
                            ].map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 hover:scale-105 transition-all duration-300 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg shadow-slate-200/50 dark:shadow-none">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center flex-shrink-0 text-white font-bold text-lg shadow-md">
                                                {item.step}
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-2">
                                                    {item.title}
                                                </h3>
                                                <p className="text-slate-600 dark:text-slate-400">{item.description}</p>
                                            </div>
                                        </div>
                                    </GlassmorphicCard>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Important Notice */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard variant="elevated" className="max-w-4xl mx-auto p-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-200/50 dark:shadow-primary-blue/10">
                        <div className="flex items-start gap-4">
                            <AlertTriangle className="h-6 w-6 text-accent-gold flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-3">
                                    Important Instructions
                                </h3>
                                <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-blue mt-1">•</span>
                                        <span>Ensure stable internet connection before starting the test</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-blue mt-1">•</span>
                                        <span>Use desktop/laptop for best experience (mobile not recommended)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-blue mt-1">•</span>
                                        <span>Do not refresh or close the browser during the test</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-blue mt-1">•</span>
                                        <span>Test will auto-submit when time expires</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-blue mt-1">•</span>
                                        <span>You can review and change answers before final submission</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            </section>
        </div>
    )
}
